import {
  FACET_CHARACTERISTICS,
  FACET_DOMAINS,
  FACET_GRADES,
  FACET_STANDARDS,
  FACET_SUBJECT,
  generateAlgoliaFiltersFromBrowseFilters,
  requestsAndOptionsForBrowseFilters
} from '@/search/browse_filters'
import type { BrowseFilterField, BrowseFilters, FacetRequest } from '@/search/browse_filters'
import { GOALS_INDEX } from '@/search/algolia_search'
import { KEYWORD_KEY } from '@/browse/browse_state'
import type { BrowseState } from '@/browse/browse_state'
import type { BrowseSearchConfig, CountQueryPlan, SearchFilterDefinition, SearchPlan } from '@/browse/browse_search'
import { GRADES } from '@/browse/grades'
import { truncatedLongQuery } from '@/util/browse'
import { filterLockedBrowseGoalsState } from '@/contentprofile/premium_locks'
import type { ContentProfileSubject } from '@/contentprofile/premium_locks'
import type { GoalCharacteristic } from '@/browse/goals/goal_characteristics'

const MAX_GOAL_RESULTS = 300
const LEVELS_OF_SUPPORT = ['0', '1', '2']
const DEFAULT_LEVEL_OF_SUPPORT = LEVELS_OF_SUPPORT[0]
const UNIVERSAL_STANDARD_SET = 'universal'
const STANDARD_SETS_ATTRIBUTE = 'standard_sets'

const ALGOLIA_ATTRIBUTE_BY_FILTER_KEY = {
  subject: FACET_SUBJECT,
  grades: FACET_GRADES,
  subjectDomain: FACET_DOMAINS,
  standard: FACET_STANDARDS,
  levelOfSupport: 'levels_of_support',
  characteristics: FACET_CHARACTERISTICS
}

// The filters the counting table can keep or drop. Level of Support is not one of them,
// so no count query ever drops it.
const COUNTING_TABLE_KEYS: BrowseFilterField[] = ['subject', 'grades', 'subjectDomain', 'standard', 'keyword', 'characteristics']

export interface GoalBrowseContext {
  standardSetsFor: (subject?: string) => string[]
  // null until the user's content profile has loaded.
  publishedSubjects: () => ContentProfileSubject[] | null
  // null until the list has loaded.
  characteristics: () => GoalCharacteristic[] | null
  isReadyForSearch: () => boolean
}

function filterKeyForAttribute(attribute: string): string {
  const matchingEntry = Object.entries(ALGOLIA_ATTRIBUTE_BY_FILTER_KEY).find(([, filterAttribute]) => filterAttribute === attribute)
  if (!matchingEntry) {
    throw new Error(`No goal filter uses the Algolia attribute "${attribute}"`)
  }
  const [filterKey] = matchingEntry
  return filterKey
}

export function browseFiltersFromState({ keyword, refinements }: BrowseState): BrowseFilters {
  return {
    subject: refinements.subject[0],
    grades: refinements.grades,
    subjectDomain: refinements.subjectDomain[0],
    standard: refinements.standard[0],
    keyword,
    characteristics: refinements.characteristics
  }
}

// The counting table lists the filters each count query keeps; the engine wants the ones it drops.
// Counts apply the BLV/DHH exclusion the results apply, so no option is enabled only by goals the
// page then hides (every Birth-3 Reading goal is BLV/DHH), except when counting characteristics,
// since choosing BLV or DHH is what lifts the exclusion.
function countQueryFromFacetRequest({ filterFields, facets }: FacetRequest): CountQueryPlan {
  const isCountingCharacteristics = facets.includes(FACET_CHARACTERISTICS)
  return {
    facetKeys: facets.map(filterKeyForAttribute),
    excludedKeys: COUNTING_TABLE_KEYS.filter((key) => !filterFields.includes(key)),
    appliesAlgoliaFilters: !isCountingCharacteristics
  }
}

// Goals counts from a hand-built table (requestsAndOptionsForBrowseFilters): it narrows grades
// and categories to a chosen standard in some combinations and not others, and that is kept
// rather than guessed at.
export function goalSearchPlan(state: BrowseState): SearchPlan {
  const { hitsFacets, facetRequests } = requestsAndOptionsForBrowseFilters(browseFiltersFromState(state))
  return {
    hitsFacetKeys: hitsFacets.map(filterKeyForAttribute),
    countQueries: facetRequests.map(countQueryFromFacetRequest)
  }
}

function blvDhhExclusionFilter({ keyword, refinements }: BrowseState): string {
  return generateAlgoliaFiltersFromBrowseFilters({ keyword, characteristics: refinements.characteristics })
}

function standardSetFacetFilters(context: GoalBrowseContext, { refinements }: BrowseState): string[][] {
  const selectedSubject = refinements.subject[0]
  const allowedStandardSets = [...context.standardSetsFor(selectedSubject), UNIVERSAL_STANDARD_SET]
  const matchesAnyAllowedSet = allowedStandardSets.map((set) => `${STANDARD_SETS_ATTRIBUTE}:${set}`)
  return [matchesAnyAllowedSet]
}

function withDefaultLevelOfSupport(state: BrowseState): BrowseState {
  if (state.refinements.levelOfSupport?.length) {
    return state
  }
  return { ...state, refinements: { ...state.refinements, levelOfSupport: [DEFAULT_LEVEL_OF_SUPPORT] } }
}

function characteristicUuids(characteristics: GoalCharacteristic[] | null): string[] | undefined {
  return characteristics?.map(({ Uuid }) => Uuid)
}

function subjectProfileFor(subjects: ContentProfileSubject[], subject: string | undefined): ContentProfileSubject | undefined {
  return subjects.find((entry) => entry.subject === subject)
}

// The sidebar and the URL check share these, so a link can only set what the sidebar offers.
export function offeredSubjects(subjects: ContentProfileSubject[]): string[] {
  return subjects.map((entry) => entry.subject)
}

export function offeredGrades(subjects: ContentProfileSubject[], subject: string | undefined): string[] {
  return subjectProfileFor(subjects, subject)?.grades ?? GRADES
}

export function offeredCategories(subjects: ContentProfileSubject[], subject: string | undefined): string[] {
  return subjectProfileFor(subjects, subject)?.categories ?? []
}

// Undefined until the profile has loaded, so a URL value is kept until it can be checked.
function offeredOnceProfileLoads(context: GoalBrowseContext, offered: (subjects: ContentProfileSubject[]) => string[]): string[] | undefined {
  const subjects = context.publishedSubjects()
  if (!subjects) {
    return undefined
  }
  return offered(subjects)
}

function selectedSubjectOf({ refinements }: BrowseState): string | undefined {
  return refinements.subject[0]
}

function asRefinement(value: string | undefined): string[] {
  if (!value) {
    return []
  }
  return [value]
}

function withoutLockedFilters(state: BrowseState, subjects: ContentProfileSubject[]): BrowseState {
  const { refinements } = state
  const unlocked = filterLockedBrowseGoalsState(subjects, {
    subject: refinements.subject[0],
    subjectDomain: refinements.subjectDomain[0],
    grades: refinements.grades
  })
  return {
    ...state,
    refinements: {
      ...refinements,
      subject: asRefinement(unlocked.subject),
      subjectDomain: asRefinement(unlocked.subjectDomain),
      grades: unlocked.grades
    }
  }
}

function normalizeGoalBrowseState(state: BrowseState, context: GoalBrowseContext): BrowseState {
  const stateWithLevelOfSupport = withDefaultLevelOfSupport(state)
  return withoutLockedFilters(stateWithLevelOfSupport, context.publishedSubjects() ?? [])
}

function goalFacets(context: GoalBrowseContext): SearchFilterDefinition[] {
  return [
    {
      key: 'subject',
      attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.subject,
      clearsOnChange: ['subjectDomain', 'standard'],
      alsoClearsWhenEmptied: ['grades'],
      validValues: () => offeredOnceProfileLoads(context, offeredSubjects)
    },
    {
      key: 'grades',
      attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.grades,
      multiple: true,
      clearsOnChange: ['standard'],
      isChipRemovalIsolated: true,
      validValues: (state) => offeredOnceProfileLoads(context, (subjects) => offeredGrades(subjects, selectedSubjectOf(state)))
    },
    {
      key: 'subjectDomain',
      attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.subjectDomain,
      clearsOnChange: ['standard'],
      isChipRemovalIsolated: true,
      validValues: (state) => offeredOnceProfileLoads(context, (subjects) => offeredCategories(subjects, selectedSubjectOf(state)))
    },
    { key: 'standard', attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.standard },
    { key: 'levelOfSupport', attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.levelOfSupport, validValues: () => LEVELS_OF_SUPPORT },
    {
      key: 'characteristics',
      attribute: ALGOLIA_ATTRIBUTE_BY_FILTER_KEY.characteristics,
      validValues: () => characteristicUuids(context.characteristics())
    }
  ]
}

// Vue 2 Browse Goals names the filter type after the value, and not at all for a subject or category.
const CHIP_FILTER_TYPE_NAMES: Record<string, string> = {
  grades: 'grade',
  characteristics: 'characteristic',
  [KEYWORD_KEY]: 'keyword',
  standard: 'standard'
}

export function goalChipRemoveButtonName(key: string, label: string): string {
  const filterTypeName = CHIP_FILTER_TYPE_NAMES[key]
  return filterTypeName ? `Remove ${label} ${filterTypeName} filter` : `Remove ${label} filter`
}

export function goalBrowseConfig(context: GoalBrowseContext): BrowseSearchConfig {
  return {
    facets: goalFacets(context),
    chipRows: [
      { label: 'Subject', keys: ['subject', 'subjectDomain'] },
      { label: 'Grades', keys: ['grades'] },
      { label: 'Characteristic', keys: ['characteristics'] },
      { label: 'Keywords', keys: [KEYWORD_KEY] },
      { label: 'Standard', keys: ['standard'] }
    ],
    hasKeywordSearch: true,
    hitsPerPage: MAX_GOAL_RESULTS,
    indexName: () => GOALS_INDEX,
    keywordForSearch: truncatedLongQuery,
    algoliaFilters: blvDhhExclusionFilter,
    fixedFacetFilters: (state) => standardSetFacetFilters(context, state),
    normalize: (state) => normalizeGoalBrowseState(state, context),
    searchPlan: goalSearchPlan,
    isReadyForSearch: context.isReadyForSearch,
    hasSearchCriteria: (state) => Boolean(selectedSubjectOf(state) || state.keyword || state.refinements.characteristics.length)
  }
}
